import React, { useState } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { inputMessageKeyframe } from "styles";
import { useField } from "formik";

// assets
import EyeOn from "assets/icons/eye_on.svg";
import EyeOff from "assets/icons/eye_off.svg";
import DangerIcon from "assets/icons/danger-icon.svg";
import SuccessIcon from "assets/icons/success-icon.svg";

const PasswordField = ({
  label,
  successMsg,
  withInstruction,
  instructionMsg,
  ...props
}) => {
  const [field, meta] = useField(props);
  const [visible, setVisible] = useState(false);
  const [type, setType] = useState("password");

  const handleToggle = () => {
    setVisible(!visible);
    setType(type === "password" ? "text" : "password");
  };

  return (
    <Wrapper>
      {label && <Label htmlFor={field.name}>{label}</Label>}

      <InputWrapper>
        <Input type={type} {...field} {...props} />
        <VisibilityIcon onClick={handleToggle}>
          <img src={visible ? EyeOn : EyeOff} />
        </VisibilityIcon>
      </InputWrapper>

      {withInstruction && instructionMsg && !meta.error ? (
        <Message className="instruction">{instructionMsg}</Message>
      ) : null}

      {meta.error && meta.touched ? (
        <Message className="error">
          <img src={DangerIcon} />
          {meta.error}
        </Message>
      ) : null}

      {!meta.error && meta.touched && successMsg ? (
        <Message className="success">
          <img src={SuccessIcon} />
          {successMsg}
        </Message>
      ) : null}
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 400;
  text-align: left;
  color: ${(props) => props.theme.colors.inputLabel};
  margin-bottom: 8px;
  line-height: 18px;
`;

const InputWrapper = styled.div`
  position: relative;
`;

const Input = styled.input`
  height: 48px;
  width: 100%;
  font-size: 16px;
  line-height: 24px;
  border: 1px solid ${(props) => props.theme.colors.inputBorder};
  border-radius: 8px;
  padding: 14px 16px;
  outline: none;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${(props) => props.theme.colors.inputActive};
  }
`;

const Message = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  font-size: 12px;
  margin-top: 6px;
  animation: ${inputMessageKeyframe} 0.5s;

  &.error {
    color: #da2e2e;
  }

  &.success {
    color: #004fec;
  }

  &.instruction {
    color: #9a9ea8;
    animation: none;
  }

  img {
    margin-right: 6px;
  }
`;

const VisibilityIcon = styled.span`
  background: white;
  position: absolute;
  right: 2px;
  padding-right: 18px;
  top: 50%;
  transform: translateY(-50%);

  img {
    height: 32px;
    width: 32px;
  }
`;

PasswordField.propTypes = {
  label: PropTypes.string,
  successMsg: PropTypes.string,
  withInstruction: PropTypes.bool,
  instructionMsg: PropTypes.string,
};

PasswordField.defaultProps = {
  label: null,
  successMsg: null,
  withInstruction: false,
};

export default PasswordField;
